/*
 *  Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  CloudProviderType,
  Provider,
  useCloudWastePoliciesQuery,
  useExternalIntegrationsFilterQuery,
  useGetAllAccountsWithDetailsQuery,
} from "~/generated/graphql";
import {
  commonRegions,
  PROVIDER,
  regions,
} from "~/constants/cloudProviderConst";
import { useResourceTypeOptions, useCategoriesOptions } from "~/hooks";
import { getAccountIconStatus, isEmpty, isNotNil, sort } from "~/tools";
import { FiltersOptions, FiltersProp } from "../types";

export function useDataOptions(filters: FiltersProp): FiltersOptions {
  const {
    options: resourceTypes,
    loading: loadingResourceTypes,
    error: resourceTypesError,
  } = useResourceTypeOptions([], filters.provider, filters.category);

  const {
    options: categories,
    loading: loadingCategories,
    error: categoriesError,
  } = useCategoriesOptions();

  const { externalIntegrations, loading: loadingExternalIntegrations } =
    useExternalIntegrations();

  const {
    cloudWastePolicies,
    loading: loadingCloudWaste,
    error: cloudWasteError,
  } = useCloudWastePolicies();

  const {
    accountsOptions,
    loading: loadingAccounts,
    error: accountsError,
  } = useAccountsOptions();

  const { regionOptions } = useRegionOptions(filters.provider);

  return {
    accountsOptions,
    regionOptions,
    resourceTypes,
    categories,
    cloudWastePolicies,
    externalIntegrations,
    error:
      accountsError ?? resourceTypesError ?? cloudWasteError ?? categoriesError,
    loadingExternalIntegrations,
    loadingCategories,
    loadingCloudWaste,
    loadingResourceTypes,
    loadingAccounts,
  };
}

function useCloudWastePolicies() {
  const { data: cloudWaste, loading, error } = useCloudWastePoliciesQuery();

  const cloudeWasteSummaryPolicies =
    cloudWaste?.cloudWasteSummary?.policies ?? [];

  const cloudWastePolicies = cloudeWasteSummaryPolicies.map((policy) => ({
    value: policy?.id,
    label: policy?.title,
  }));

  return { cloudWastePolicies, loading, error };
}

function useAccountsOptions(providers: FiltersProp["provider"] = []) {
  const { data, loading, error } = useGetAllAccountsWithDetailsQuery();

  const accounts = (data?.accounts ?? []).filter(isNotNil);

  const accountsOptions = accounts
    .filter(
      (account) =>
        isEmpty(providers) ||
        providers.find((filtersProvider) =>
          [account.provider, account.providerType].includes(filtersProvider)
        )
    )
    .map((account) => ({
      value: account.id ?? account.providerId,
      label: account.displayName ?? account.name,
      status: getAccountIconStatus({
        provider: account.provider,
        active: account.activated,
        status: account.status ?? "",
        providerType: account.providerType ?? CloudProviderType.Cloud,
      }),
    }));

  return {
    accountsOptions,
    loading,
    error,
  };
}

function useRegionOptions(providers: FiltersProp["provider"] = []) {
  const mappedProviders = mapProviders(providers);
  const mappedRegions =
    mappedProviders?.flatMap((provider) => regions.get(provider) ?? "") ?? [];

  const regionOptions = sort([...commonRegions, ...mappedRegions]).map(
    (region) => ({
      value: region,
      label: region,
    })
  );

  return { regionOptions };
}

function useExternalIntegrations() {
  const { data, ...queryState } = useExternalIntegrationsFilterQuery();

  const externalIntegrations =
    data?.externalIntegrations?.integrations?.map((integration) => ({
      value: integration?.id ?? "",
      label: integration?.name ?? "",
    })) ?? [];

  return {
    externalIntegrations,
    ...queryState,
  };
}

function mapProviders(input: FiltersProp["provider"]): PROVIDER[] | undefined {
  if (input == null) {
    return undefined;
  }

  return input
    .map((provider) => {
      switch (provider) {
        case Provider.Aws:
          return PROVIDER.AWS;
        case Provider.Gcp:
          return PROVIDER.GCP;
        case Provider.Azure:
          return PROVIDER.AZURE;
        case Provider.Ibmcloud:
          return PROVIDER.IBMCLOUD;
        default:
          return undefined;
      }
    })
    .filter(isNotNil);
}
